import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    hcaptcha?: {
      render: (
        container: HTMLElement,
        opts: {
          sitekey: string;
          rqdata?: string;
          callback?: (token: string) => void;
          'error-callback'?: (err: unknown) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark';
          size?: 'normal' | 'compact' | 'invisible';
        },
      ) => string;
      execute: (widgetId?: string) => void;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    onHCaptchaReady?: () => void;
  }
}

interface Props {
  siteKey: string;
  /** "rqdata" — зашифрованный challenge от Riot */
  rqData: string;
  onVerified: (token: string) => void;
  onError?: (err: unknown) => void;
}

const HCAPTCHA_SRC = 'https://js.hcaptcha.com/1/api.js?onload=onHCaptchaReady&render=explicit';

/** Singleton-loader для hcaptcha script */
function loadHCaptcha(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.hcaptcha) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src^="${HCAPTCHA_SRC.split('?')[0]}"]`);
    if (existing) {
      window.onHCaptchaReady = () => resolve();
      return;
    }
    window.onHCaptchaReady = () => resolve();
    const s = document.createElement('script');
    s.src = HCAPTCHA_SRC;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('Failed to load hCaptcha'));
    document.head.appendChild(s);
  });
}

export function HCaptcha({ siteKey, rqData, onVerified, onError }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadHCaptcha()
      .then(() => {
        if (cancelled || !hostRef.current || !window.hcaptcha) return;
        try {
          widgetIdRef.current = window.hcaptcha.render(hostRef.current, {
            sitekey: siteKey,
            rqdata: rqData,
            theme: 'dark',
            size: 'normal',
            callback: (token) => onVerified(token),
            'error-callback': (err) => {
              console.error('hCaptcha error:', err);
              setStatus('error');
              onError?.(err);
            },
            'expired-callback': () => {
              setStatus('ready');
            },
          });
          setStatus('ready');
        } catch (err) {
          console.error('hCaptcha render failed:', err);
          setStatus('error');
          onError?.(err);
        }
      })
      .catch((err) => {
        console.error(err);
        setStatus('error');
        onError?.(err);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.hcaptcha) {
        try {
          window.hcaptcha.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
      }
    };
  }, [siteKey, rqData, onVerified, onError]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div ref={hostRef} />
      {status === 'loading' && (
        <div className="text-xs text-valo-muted animate-pulse">Загрузка проверки...</div>
      )}
      {status === 'error' && (
        <div className="text-xs text-valo-red">Ошибка загрузки hCaptcha</div>
      )}
    </div>
  );
}
